import { FormaliteJsonldFormaliteUpdate } from '@europrocurement/l2d-domain/openApi/ApiFormalite';
import { useState } from 'react';
import {
    CategoriesFormalite,
    categoriesFormalitySelector,
    useFormaliteService,
} from '@europrocurement/l2d-domain';
import { FiltersOpts, SearchOpts } from '@europrocurement/flexy-datatable';
import { OfferGroup } from '@europrocurement/l2d-domain/reducers/offre/slices/offerGroupSlice';
import { useSelector } from 'react-redux';
import { useApiRequest } from '@europrocurement/l2d-hooks';
import {
    Formality,
    UPDATE_ITEM_CTX,
    useFormalitiesCtx,
    useFormalitiesCtxDispatch,
} from '@europrocurement/l2d-modules';
import useGroup from './useGroup';

const useCompatibility = () => {
    const { formalityModel: model } = useFormaliteService();
    const dataSetter = useApiRequest();
    const { getFormalities } = useFormalitiesCtx();
    const formalityDispatch = useFormalitiesCtxDispatch();
    const [searchTerm, setSearchTerm] = useState('');
    const [filters, setFilters] = useState({});
    const { groups } = useGroup();
    const categoriesDatasource = useSelector(categoriesFormalitySelector);

    const sortFormalities = (a: Formality, b: Formality) => {
        const categoryA = a?.category?.id || null;
        const categoryB = b?.category?.id || null;

        if (categoryA === null) {
            return 1;
        }

        if (categoryB === null) {
            return -1;
        }

        if (categoryA === categoryB) {
            return 0;
        }

        return categoryA < categoryB ? -1 : 1;
    };

    const getFilteredFormalities = () => {
        const categoryId = filters?.categorieId
            ? Number(filters?.categorieId as number)
            : undefined;
        const groupementId = filters?.groupement
            ? Number(filters?.groupement as number)
            : undefined;

        return getFormalities()
            .filter((formality) => {
                if (categoryId && formality?.category?.id !== categoryId) {
                    return false;
                }

                if (groupementId && !formality?.groupements.includes(groupementId)) {
                    return false;
                }

                return true;
            })
            .sort(sortFormalities);
    };

    const formalities = getFormalities().sort(sortFormalities);

    const toggleCompatibility = (formality: Formality, compatibility: Formality) => {
        const formalityId = formality?.id as unknown as number;
        const compatibilityId = compatibility?.id as unknown as number;
        const formalityCompatibilities = formality.compatibilites
            ? [...formality.compatibilites]
            : [];
        const compatibilityCompatibilities = compatibility.compatibilites
            ? [...compatibility.compatibilites]
            : [];

        if (formalityCompatibilities.indexOf(compatibilityId) === -1) {
            formalityCompatibilities.push(compatibilityId);
        } else {
            formalityCompatibilities.splice(formalityCompatibilities.indexOf(compatibilityId), 1);
        }

        if (compatibilityCompatibilities.indexOf(formalityId) === -1) {
            compatibilityCompatibilities.push(formalityId);
        } else {
            compatibilityCompatibilities.splice(
                compatibilityCompatibilities.indexOf(formalityId),
                1,
            );
        }

        if (formalityDispatch) {
            formalityDispatch({
                type: UPDATE_ITEM_CTX,
                payload: {
                    ...formality,
                    compatibilites: formalityCompatibilities,
                },
            });

            formalityDispatch({
                type: UPDATE_ITEM_CTX,
                payload: {
                    ...compatibility,
                    compatibilites: compatibilityCompatibilities,
                },
            });
        }

        const payload: FormaliteJsonldFormaliteUpdate = {
            ...formality,
            compatibilities: formalityCompatibilities,
        };

        dataSetter.request(
            model.update({
                id: formalityId,
                formaliteJsonldFormaliteUpdate: payload,
            }),
            {
                withToaster: false,
            },
        );

        return { formalityCompatibilities, compatibilityCompatibilities };
    };

    const filterOpts: FiltersOpts = {
        filters: [
            {
                label: 'Groupement',
                field: 'groupement',
                type: 'staticSelect',
                options: groups.map((group: OfferGroup) => ({
                    value: group.id,
                    label: group.libelle,
                })),
                renderFilterList: (value) => {
                    const t: OfferGroup = groups.find((group: OfferGroup) => group.id === value);

                    return t?.libelle as string;
                },
            },
            {
                label: 'Catégorie',
                field: 'categorieId',
                type: 'staticSelect',
                options: categoriesDatasource.data.map((category: CategoriesFormalite) => ({
                    value: category.id,
                    label: category.libelle,
                })),
                renderFilterList: (value) =>
                    categoriesDatasource.data.find(
                        (category: CategoriesFormalite) => category.id === value,
                    )?.libelle,
            },
        ],
        activeFilters: filters,
        onFilterChange: (key: string, value) => {
            const tmp: Record<string, unknown> = { ...filters };
            tmp[key] = value;

            setFilters(tmp);
        },
        onFilterDelete: (key: string) => {
            const tmp: Record<string, unknown> = { ...filters };

            if (tmp[key]) {
                delete tmp[key];
            }
            setFilters(tmp);
        },
        onFiltersClear: () => {
            setFilters({});
        },
    };

    const searchOpts: SearchOpts = {
        onSearchChange: (newTerm) => {
            setSearchTerm(newTerm);
        },
        search: searchTerm,
        filtersOpts: filterOpts,
    };

    const getDistinctCategoriesMap = (formalitiesItems: Formality[]) => {
        const hasCategory: number[] = [];
        const distinctCategories = new Map();

        formalitiesItems.forEach((formality: Formality) => {
            const categoryId = formality?.category?.id || 0;

            if (hasCategory.indexOf(categoryId) === -1) {
                hasCategory.push(categoryId);
                distinctCategories.set(categoryId, {
                    id: categoryId,
                    label: formality?.category?.label || '-',
                    iconeName: formality?.category?.iconeName || '',
                    count: 0,
                });
            }

            distinctCategories.set(categoryId, {
                id: categoryId,
                label: formality?.category?.label || '-',
                iconeName: formality?.category?.iconeName || '',
                count: distinctCategories.get(categoryId).count + 1,
            });
        });

        return distinctCategories;
    };

    const getDistinctCategories = (formalitiesItems: Formality[]) =>
        Array.from(getDistinctCategoriesMap(formalitiesItems)).map((item) => item[1]);

    return {
        formalities,
        searchOpts,
        toggleCompatibility,
        getFilteredFormalities,
        getDistinctCategories,
        getDistinctCategoriesMap,
    };
};

export default useCompatibility;
